import React, { useState, useEffect } from "react";
import { get, put } from "../../../../utils/api";

interface Link {
  to: string;
  name: string;
}

interface MenuSection {
  id: number;
  title: string;
  links: Link[];
}

const MenuEditor = () => {
  const [menuSections, setMenuSections] = useState<MenuSection[]>([]);
  const [editState, setEditState] = useState<{ [key: number]: boolean }>({});

  useEffect(() => {
    get(`settings/details`, { settings_id: 1 })
      .then((response: any) => {
        console.log("Fetched side_menu data:", response.data.side_menu);
        setMenuSections(response.data.side_menu);
      })
      .catch((error) => console.error("Failed to fetch settings:", error));
  }, []);

  const handleFocus = (id: number) => {
    setEditState({ ...editState, [id]: true });
  };

  const handleBlur = (id: number) => {
    setEditState({ ...editState, [id]: false });
  };

  const handleLinkChange = (
    sectionIndex: number,
    linkIndex: number,
    field: keyof Link,
    value: string
  ) => {
    const updatedSections = [...menuSections];
    updatedSections[sectionIndex].links[linkIndex] = {
      ...updatedSections[sectionIndex].links[linkIndex],
      [field]: value,
    };
    setMenuSections(updatedSections);
  };

  const handleTitleChange = (sectionIndex: number, newTitle: string) => {
    const updatedSections = [...menuSections];
    updatedSections[sectionIndex].title = newTitle;
    setMenuSections(updatedSections);
  };

  const handleAddLink = (sectionIndex: number) => {
    const newSections = [...menuSections];
    newSections[sectionIndex].links.push({ to: "", name: "" });
    setMenuSections(newSections);
  };

  const handleRemoveLink = (sectionIndex: number, linkIndex: number) => {
    const updatedSections = [...menuSections];
    updatedSections[sectionIndex].links.splice(linkIndex, 1);
    setMenuSections(updatedSections);
  };

  const handleAddSection = () => {
    const newSectionId = menuSections.length + 1;
    setMenuSections([
      ...menuSections,
      { id: newSectionId, title: "New Section", links: [] },
    ]);
    setEditState({ ...editState, [newSectionId]: true });
  };

  const handleRemoveSection = (sectionIndex: number) => {
    const updatedSections = [...menuSections];
    updatedSections.splice(sectionIndex, 1);
    setMenuSections(updatedSections);
  };

  const handleSubmit = () => {
    put(`settings/${1}`, { side_menu: menuSections })
      .then(() => console.log("Changes saved successfully"))
      .catch((error) => console.error("Failed to save changes:", error));
  };

  const styles = {
    container: {
      padding: "24px",
    //   backgroundColor: "#f2f4f5",
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      maxWidth: "600px",
      margin: "0 auto",
      borderRadius: "10px",
    //   boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    },
    section: {
      marginBottom: "16px",
      backgroundColor: "#ffffff",
      padding: "16px",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
    input: {
      padding: "10px",
      border: "1px solid #e0e0e0",
      borderRadius: "6px",
      width: "100%",
      fontSize: "14px",
      color: "#333",
      backgroundColor: "#f9f9f9",
      marginBottom: "12px",
    },
    button: {
      backgroundColor: "#4a90e2",
      color: "white",
      fontWeight: "500",
      padding: "6px 12px",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      margin: "0 5px 10px 0",
      fontSize: "14px",
      transition: "background-color 0.3s ease, transform 0.2s ease",
    },
    saveButton: {
      backgroundColor: "#1d72b8",
      color: "white",
      fontWeight: "bold",
      padding: "10px 0",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      fontSize: "16px",
      marginTop: "16px",
      width: "100%",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      transition: "background-color 0.3s ease, transform 0.2s ease",
    },
    buttonHover: {
      backgroundColor: "#357ABD",
    },
    buttonActive: {
      transform: "scale(0.98)",
    },
    title: {
      fontWeight: "bold",
      fontSize: "16px",
      border: "none",
      width: "100%",
      display: "block",
      backgroundColor: "transparent",
      outline: "none",
      marginBottom: "10px",
    },
    linkContainer: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "12px",
    },
  };

  return (
    <div style={styles.container}>
      {menuSections.map((section, sectionIndex) => (
        <div key={section.id} style={styles.section}>
          <input
            type="text"
            value={section.title}
            onChange={(e) => handleTitleChange(sectionIndex, e.target.value)}
            style={styles.title}
            onFocus={() => handleFocus(section.id)}
            onBlur={() => handleBlur(section.id)}
            placeholder="Section Title"
          />
          {section.links.map((link, linkIndex) => (
            <div key={`${sectionIndex}-${linkIndex}`} style={styles.linkContainer}>
              <input
                type="text"
                value={link.name}
                style={{ ...styles.input, flex: 1 }}
                onChange={(e) =>
                  handleLinkChange(
                    sectionIndex,
                    linkIndex,
                    "name",
                    e.target.value
                  )
                }
                placeholder="Link Name"
              />
              <input
                type="text"
                value={link.to}
                style={{ ...styles.input, flex: 2 }}
                onChange={(e) =>
                  handleLinkChange(
                    sectionIndex,
                    linkIndex,
                    "to",
                    e.target.value
                  )
                }
                placeholder="URL"
              />
              <button
                style={styles.button}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = styles.buttonHover.backgroundColor)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#4a90e2")}
                onMouseDown={(e) => (e.currentTarget.style.transform = styles.buttonActive.transform)}
                onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
                onClick={() => handleRemoveLink(sectionIndex, linkIndex)}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            style={styles.button}
            onClick={() => handleAddLink(sectionIndex)}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = styles.buttonHover.backgroundColor)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#4a90e2")}
          >
            Add Link
          </button>
          <button
            style={styles.button}
            onClick={() => handleRemoveSection(sectionIndex)}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = styles.buttonHover.backgroundColor)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#4a90e2")}
          >
            Remove Section
          </button>
        </div>
      ))}
      <button style={styles.button} onClick={handleAddSection}>
        Add Section
      </button>
      <button
        style={styles.saveButton}
        onClick={handleSubmit}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#165a7b")}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#1d72b8")}
      >
        Save All Changes
      </button>
    </div>
  );
};

export default MenuEditor;
